(function () {
  // The overlay drawer and the cart page render the same markup and share this
  // script. Both are roots for rendering; only the overlay one opens and closes.
  var ROOT_SELECTOR = '.oddit-cart-drawer';
  var PAGE_MODIFIER = 'oddit-cart-drawer--page';

  if (window.__odditCartDrawerBooted) return;

  var roots = Array.prototype.slice.call(document.querySelectorAll(ROOT_SELECTOR));
  if (roots.length === 0) return;

  window.__odditCartDrawerBooted = true;

  var drawer = null;
  for (var r = 0; r < roots.length; r++) {
    if (!roots[r].classList.contains(PAGE_MODIFIER)) {
      drawer = roots[r];
      break;
    }
  }

  // ---------- State ----------
  var abortController = new AbortController();
  var signal = abortController.signal;
  var isOpen = false;
  var lastFocusedElement = null;
  var activeCurrency = null;
  var cachedCart = null;
  var inFlight = false;

  // Collect matches across every root, since a page can hold both the overlay
  // drawer and the cart page section.
  function queryAll(selector) {
    var out = [];
    for (var i = 0; i < roots.length; i++) {
      Array.prototype.push.apply(out, roots[i].querySelectorAll(selector));
    }
    return out;
  }

  // ---------- Currency / Money ----------
  function getActiveCurrency() {
    if (activeCurrency) return activeCurrency;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return 'USD';
  }

  function formatMoney(cents) {
    var currency = getActiveCurrency();
    var amount = (Number(cents) || 0) / 100;
    // Whole amounts render without cents to match the design ($32, not $32.00).
    var fractionDigits = amount % 1 === 0 ? 0 : 2;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
      }).format(amount);
    } catch (e) {
      return amount.toFixed(fractionDigits) + ' ' + currency;
    }
  }

  // ---------- Utils ----------
  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getFocusableElements(container) {
    if (!container) return [];
    var selector = [
      'a[href]',
      'area[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    return Array.prototype.slice.call(container.querySelectorAll(selector))
      .filter(function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      });
  }

  function trapFocus(e) {
    if (!isOpen || !drawer || e.key !== 'Tab') return;
    var panel = drawer.querySelector('.oddit-cart-drawer__panel');
    var focusable = getFocusableElements(panel);
    if (focusable.length === 0) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function lockBodyScroll() {
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '';
  }

  function unlockBodyScroll() {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    releaseRebuyScrollLock();
  }

  function releaseRebuyScrollLock() {
    try {
      if (window.Rebuy && window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.hide === 'function') {
        window.Rebuy.SmartCart.hide();
      }
    } catch (e) {}
    document.body.classList.remove('rebuy-cart-visible', 'rebuy-modal-visible');
  }

  // ---------- Open / Close ----------
  function openDrawer() {
    if (isOpen || !drawer) return;
    isOpen = true;
    lastFocusedElement = document.activeElement;
    drawer.style.display = '';
    // Force reflow for transition
    void drawer.offsetWidth;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockBodyScroll();
    // Refresh cart quietly on open
    refreshCart().catch(function () {});
    // Move focus to the panel itself rather than the first control. Focusing a
    // button programmatically right after a click makes Chrome paint its
    // :focus-visible ring; a tabindex="-1" container never shows one, and Tab
    // still lands on the close button via trapFocus().
    var panel = drawer.querySelector('.oddit-cart-drawer__panel');
    if (panel) {
      panel.setAttribute('tabindex', '-1');
      panel.focus();
    }
  }

  function closeDrawer() {
    if (!isOpen || !drawer) return;
    isOpen = false;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    unlockBodyScroll();
    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
    lastFocusedElement = null;
  }

  function toggleDrawer() {
    if (isOpen) {
      closeDrawer();
    } else {
      openDrawer();
    }
  }

  // ---------- Cart API ----------
  function jsonFetch(url, options) {
    var opts = options || {};
    var headers = Object.assign({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }, opts.headers || {});
    return fetch(url, {
      method: opts.method || 'GET',
      headers: headers,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      credentials: 'same-origin'
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var err = new Error((data && (data.description || data.message)) || 'Cart request failed');
          err.status = res.status;
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  function getCart() {
    return jsonFetch('/cart.js', { method: 'GET' }).then(function (cart) {
      cachedCart = cart;
      if (cart && cart.currency) activeCurrency = cart.currency;
      return cart;
    });
  }

  function addToCart(variantId, quantity, properties, selling_plan=null) {
    quantity = quantity == null ? 1 : quantity;
    properties = properties || {};
    var body = {
      id: Number(variantId),
      quantity: quantity,
      properties: properties
    };
    if(selling_plan){
      body.selling_plan = selling_plan
    }
    inFlight = true;
    return jsonFetch('/cart/add.js', { method: 'POST', body: body })
      .then(function () { return getCart(); })
      .then(function (cart) {
        renderCart(cart);
        inFlight = false;
        return cart;
      })
      .catch(function (err) {
        inFlight = false;
        throw err;
      });
  }

  function addMultipleToCart(items) {
    var normalized = (items || []).map(function (item) {
      return {
        id: Number(item.id),
        quantity: item.quantity == null ? 1 : item.quantity,
        properties: item.properties || {}
      };
    });
    inFlight = true;
    return jsonFetch('/cart/add.js', { method: 'POST', body: { items: normalized } })
      .then(function () { return getCart(); })
      .then(function (cart) {
        renderCart(cart);
        inFlight = false;
        return cart;
      })
      .catch(function (err) {
        inFlight = false;
        throw err;
      });
  }

  function updateLineItemQuantity(lineItemKey, quantity) {
    inFlight = true;
    return jsonFetch('/cart/change.js', {
      method: 'POST',
      body: { id: lineItemKey, quantity: quantity }
    }).then(function (cart) {
      cachedCart = cart;
      if (cart && cart.currency) activeCurrency = cart.currency;
      renderCart(cart);
      inFlight = false;
      return cart;
    }).catch(function (err) {
      inFlight = false;
      throw err;
    });
  }

  function removeFromCart(lineItemKey) {
    return updateLineItemQuantity(lineItemKey, 0);
  }

  function updateLineItemToSubscription(lineItemKey, sellingPlanId) {
    inFlight = true;
    return jsonFetch('/cart/change.js', {
      method: 'POST',
      body: { id: lineItemKey, selling_plan: sellingPlanId }
    }).then(function (cart) {
      cachedCart = cart;
      if (cart && cart.currency) activeCurrency = cart.currency;
      renderCart(cart);
      inFlight = false;
      return cart;
    }).catch(function (err) {
      inFlight = false;
      throw err;
    });
  }

  function refreshCart() {
    return getCart().then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  // ---------- Line Item HTML ----------
  function buildLineItemHtml(item) {
    var key = escapeHtml(item.key);
    var url = escapeHtml(item.url || '#');
    var title = escapeHtml(item.product_title || item.title || '');
    var price = formatMoney(item.final_line_price != null ? item.final_line_price : item.line_price);
    var quantity = Number(item.quantity) || 0;

    var imageHtml = '';
    if (item.image) {
      var imgSrc = item.image;
      if (typeof imgSrc === 'string' && imgSrc.indexOf('{width}') === -1) {
        // Ensure a reasonable size
        imgSrc = imgSrc.replace(/(\.(?:jpg|jpeg|png|webp|gif))(\?.*)?$/i, '_180x$1$2');
      }
      imageHtml =
        '<img class="oddit-cart-drawer__item-image" src="' + escapeHtml(imgSrc) + '"' +
        ' alt="' + escapeHtml(title) + '" loading="lazy" width="90" height="90">';
    }

    var optionsHtml = '';
    if (item.options_with_values && item.options_with_values.length > 0 &&
        !(item.options_with_values.length === 1 && item.options_with_values[0].value === 'Default Title')) {
      optionsHtml = '<div class="oddit-cart-drawer__item-labels">';
      for (var i = 0; i < item.options_with_values.length; i++) {
        var opt = item.options_with_values[i];
        optionsHtml +=
          '<div class="oddit-cart-drawer__item-label">' +
            '<span class="oddit-cart-drawer__item-label-key">' + escapeHtml(opt.name) + ':</span> ' +
            '<span class="oddit-cart-drawer__item-label-value">' + escapeHtml(opt.value) + '</span>' +
          '</div>';
      }
      optionsHtml += '</div>';
    }

    var removeSvg =
      '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<g opacity="0.6">' +
          '<path d="M10.5 8.25V12.75M7.5 8.25V12.75M4.5 5.25V14.25C4.5 14.6478 4.65804 15.0294 4.93934 15.3107C5.22064 15.592 5.60218 15.75 6 15.75H12C12.3978 15.75 12.7794 15.592 13.0607 15.3107C13.342 15.0294 13.5 14.6478 13.5 14.25V5.25M3 5.25H15M5.25 5.25L6.75 2.25H11.25L12.75 5.25" stroke="#282B2E" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</g>' +
      '</svg>';

    var minusSvg =
      '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M4.5 9H13.5" stroke="#2D2120" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';

    var plusSvg =
      '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M9 4.5V13.5M4.5 9H13.5" stroke="#2D2120" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';

    return (
      '<article class="oddit-cart-drawer__item" data-line-item data-line-item-key="' + key + '" data-line-key="' + key + '">' +
        '<div class="oddit-cart-drawer__item-image-wrap">' + imageHtml + '</div>' +
        '<div class="oddit-cart-drawer__item-details">' +
          '<div class="oddit-cart-drawer__item-header">' +
            '<h3 class="oddit-cart-drawer__item-title">' +
              '<a href="' + url + '">' + title + '</a>' +
            '</h3>' +
            '<button type="button" class="oddit-cart-drawer__item-remove" ' +
              'aria-label="Remove item from cart" ' +
              'data-remove-item data-remove-line ' +
              'data-line-item-key="' + key + '" data-line-key="' + key + '">' +
              removeSvg +
            '</button>' +
          '</div>' +
          optionsHtml +
          '<p class="oddit-cart-drawer__item-price" data-line-item-price>' + price + '</p>' +
          '<div class="oddit-cart-drawer__quantity" role="group" aria-label="Quantity">' +
            '<button type="button" class="oddit-cart-drawer__quantity-btn" ' +
              'aria-label="Decrease quantity" ' +
              'data-quantity-decrease data-quantity-action="decrease" ' +
              'data-line-item-key="' + key + '" data-line-key="' + key + '">' +
              minusSvg +
            '</button>' +
            '<span class="oddit-cart-drawer__quantity-value" aria-live="polite">' +
              '<span class="oddit-cart-drawer__quantity-digit" data-line-item-quantity>' + quantity + '</span>' +
            '</span>' +
            '<button type="button" class="oddit-cart-drawer__quantity-btn" ' +
              'aria-label="Increase quantity" ' +
              'data-quantity-increase data-quantity-action="increase" ' +
              'data-line-item-key="' + key + '" data-line-key="' + key + '">' +
              plusSvg +
            '</button>' +
          '</div>' +
        '</div>' +
      '</article>'
    );
  }

  // ---------- Render Cart ----------
  function renderCart(cart) {
    if (!cart) return;
    cachedCart = cart;
    if (cart.currency) activeCurrency = cart.currency;

    var itemCount = Number(cart.item_count) || 0;
    var hasItems = itemCount > 0;

    // Item counts
    var countEls = queryAll('[data-cart-count], .oddit-cart-drawer__count, .oddit-cart-drawer__subtotal-count');
    countEls.forEach(function (el) {
      el.textContent = String(itemCount);
    });

    // Subtotal
    var subtotalEls = queryAll('[data-cart-subtotal]');
    subtotalEls.forEach(function (el) {
      el.textContent = formatMoney(cart.total_price);
    });

    // Line items — inject HTML into every [data-cart-items] container.
    // IMPORTANT: We only touch [data-cart-items] — NEVER an ancestor that
    // could contain [data-cart-additional-checkout] (Shopify's accelerated
    // checkout web component must not be re-parented or re-rendered).
    var itemContainers = queryAll('[data-cart-items]');
    var itemsHtml = '';
    if (cart.items && cart.items.length) {
      for (var i = 0; i < cart.items.length; i++) {
        itemsHtml += buildLineItemHtml(cart.items[i]);
      }
    }
    itemContainers.forEach(function (container) {
      container.innerHTML = itemsHtml;
    });

    // Toggle empty vs filled states
    var emptyStates = queryAll('[data-cart-empty]');
    var filledStates = queryAll('[data-cart-filled]');
    var filledFooters = queryAll('[data-cart-filled-footer]');
    emptyStates.forEach(function (el) {
      if (hasItems) el.setAttribute('hidden', ''); else el.removeAttribute('hidden');
    });
    filledStates.forEach(function (el) {
      if (hasItems) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
    });
    filledFooters.forEach(function (el) {
      if (hasItems) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
    });

    // Also support [data-cart-state="empty|filled"] pattern if used
    var stateContainers = queryAll('[data-cart-state]');
    stateContainers.forEach(function (el) {
      var state = el.getAttribute('data-cart-state');
      var shouldShow = (hasItems && state === 'filled') || (!hasItems && state === 'empty');
      if (shouldShow) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
    });

    // Progress bar
    updateProgressBar(cart);

    // Cross-sells are Liquid-rendered off the cart, so they need a server round-trip
    refreshCrossSell(cart);

    // Filter upsells/cross-sells for products already in cart
    filterUpsells(cart);

    // Dispatch cart:updated for other sections (header count badge, etc.)
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
  }

  // ---------- Progress Bar ----------
  // Each root carries its own thresholds and message templates, so the drawer and
  // the cart page can be configured independently. This mirrors the Liquid in
  // oddit-cart-drawer.liquid / oddit-main-cart.liquid — keep the three in step.
  function updateProgressBar(cart) {
    var totalCents = Number(cart.total_price) || 0;
    for (var i = 0; i < roots.length; i++) {
      updateProgressBarForRoot(roots[i], totalCents);
    }
  }

  function updateProgressBarForRoot(root, totalCents) {
    var shippingCents = parseInt(root.getAttribute('data-free-shipping-threshold'), 10) || 0;
    var giftCents = parseInt(root.getAttribute('data-free-gift-threshold'), 10) || 0;

    // A gift milestone only exists when it sits above the shipping milestone.
    // Otherwise the bar has a single segment ending at free shipping.
    var giftActive = giftCents > shippingCents;
    var shippingReached = shippingCents > 0 && totalCents >= shippingCents;
    var giftReached = giftActive && totalCents >= giftCents;

    var percentage = 0;
    if (shippingCents > 0) {
      if (giftActive) {
        if (giftReached) {
          percentage = 100;
        } else if (shippingReached) {
          percentage = 50 + ((totalCents - shippingCents) / (giftCents - shippingCents)) * 50;
        } else {
          percentage = (totalCents / shippingCents) * 50;
        }
      } else if (shippingReached) {
        percentage = 100;
      } else {
        percentage = (totalCents / shippingCents) * 100;
      }
    }
    percentage = Math.max(0, Math.min(percentage, 100));

    // Fill width — support both data attribute and BEM class selectors
    var fills = root.querySelectorAll('[data-progress-fill], .oddit-cart-drawer__shipping-fill, .oddit-cart-drawer__progress-fill');
    fills.forEach(function (el) {
      el.style.width = percentage + '%';
    });

    // Update aria-valuenow on progressbar
    var progressBars = root.querySelectorAll('[role="progressbar"]');
    progressBars.forEach(function (el) {
      el.setAttribute('aria-valuenow', String(Math.round(percentage)));
    });

    // Update milestone reached classes
    var progressWrappers = root.querySelectorAll('.oddit-cart-drawer__progress');
    progressWrappers.forEach(function (wrap) {
      var milestones = wrap.querySelectorAll('.oddit-cart-drawer__milestone');
      if (milestones.length > 0) {
        var firstIcon = milestones[0].querySelector('.oddit-cart-drawer__milestone-icon');
        if (firstIcon) {
          firstIcon.classList.toggle('oddit-cart-drawer__milestone-icon--reached', shippingReached);
        }
      }
      if (milestones.length > 1) {
        var secondIcon = milestones[1].querySelector('.oddit-cart-drawer__milestone-icon');
        if (secondIcon) {
          secondIcon.classList.toggle('oddit-cart-drawer__milestone-icon--reached', giftReached);
        }
      }
    });

    // Progress message text — templates come from the section settings so the
    // merchant's copy (and translations) survive a client-side cart update. The
    // empty state carries its own static copy and is left alone.
    var messageEls = Array.prototype.slice
      .call(root.querySelectorAll('[data-progress-message], .oddit-cart-drawer__shipping-text, .oddit-cart-drawer__progress-message'))
      .filter(function (el) { return !el.closest('[data-cart-empty]'); });

    if (shippingCents > 0 && messageEls.length > 0) {
      var template = '';
      var amountCents = 0;
      if (giftReached) {
        template = root.getAttribute('data-progress-message-complete') || '';
      } else if (giftActive && shippingReached) {
        template = root.getAttribute('data-progress-message-unlocked') || '';
        amountCents = giftCents - totalCents;
      } else if (shippingReached) {
        template = root.getAttribute('data-progress-message-complete') || '';
      } else {
        template = root.getAttribute('data-progress-message-active') || '';
        amountCents = shippingCents - totalCents;
      }
      if (template) {
        var msg = template.split('$X').join(escapeHtml(formatMoney(Math.max(amountCents, 0))));
        messageEls.forEach(function (el) {
          el.innerHTML = msg;
        });
      }
    }
  }

  // ---------- Cross-sell ----------
  // The cross-sell cards come out of Liquid, walked from the products in the cart
  // (metafield source) or from the Recommendations API keyed on the cart. A
  // client-side add changes the cart but not that markup — hence the reload the
  // shopper otherwise needed. Re-fetch the section and swap the container in.
  //
  // Keyed on the set of product ids, not the whole cart: a quantity bump or a
  // subtotal change cannot change which cross-sells are shown, so it skips the
  // request. The first call after boot always fetches, since the page's markup may
  // have come from a cached render of a different cart.
  var crossSellProductIds = null;

  function cartProductIdSignature(cart) {
    return (cart.items || [])
      .map(function (item) { return String(item.product_id); })
      .sort()
      .join(',');
  }

  function refreshCrossSell(cart) {
    if (queryAll('[data-cross-sell-container]').length === 0) return;

    var signature = cartProductIdSignature(cart);
    if (signature === crossSellProductIds) return;
    crossSellProductIds = signature;

    // Section Rendering API: each root carries its own section id.
    var sectionIds = roots
      .map(function (root) { return root.id; })
      .filter(Boolean);
    if (sectionIds.length === 0) return;

    fetch('/?sections=' + encodeURIComponent(sectionIds.join(',')), { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (sections) {
        var parser = new DOMParser();
        roots.forEach(function (root) {
          var html = sections[root.id];
          var current = root.querySelector('[data-cross-sell-container]');
          if (!html || !current) return;
          var fresh = parser
            .parseFromString(html, 'text/html')
            .querySelector('[data-cross-sell-container]');
          if (!fresh) return;
          // innerHTML, so the container element the click handlers delegate
          // through stays in place.
          current.innerHTML = fresh.innerHTML;
        });
        filterUpsells(cart);
      })
      .catch(function () {});
  }

  // ---------- Upsell / Cross-sell Filter ----------
  function filterUpsells(cart) {
    var inCartProductIds = {};
    if (cart.items && cart.items.length) {
      for (var i = 0; i < cart.items.length; i++) {
        inCartProductIds[String(cart.items[i].product_id)] = true;
      }
    }
    var cards = queryAll('[data-product-id]');
    cards.forEach(function (card) {
      var pid = card.getAttribute('data-product-id');
      if (pid && inCartProductIds[pid]) {
        card.setAttribute('hidden', '');
      } else {
        card.removeAttribute('hidden');
      }
    });
  }

  // ---------- Button Loading Helpers ----------
  function setBusy(btn) {
    if (!btn) return;
    var restWidth = btn.getBoundingClientRect().width;
    if (restWidth > 0) btn.style.minWidth = restWidth + 'px';
    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
  }

  function clearBusy(btn) {
    if (!btn) return;
    btn.disabled = false;
    btn.classList.remove('is-loading');
    btn.removeAttribute('aria-busy');
    btn.style.minWidth = '';
  }

  // ---------- Event Wiring ----------
  function bindStaticListeners() {
    // Close buttons
    var closeButtons = queryAll('[data-cart-drawer-close]');
    closeButtons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        closeDrawer();
      }, { signal: signal });
    });

    // Cross-sell variant selects: keep the card's price on the chosen variant.
    // The add button reads the select directly, so only the price needs syncing.
    // Bound per root, since the cart page renders this markup without an overlay
    // drawer — `drawer` is null there.
    roots.forEach(function (root) {
      root.addEventListener('change', function (e) {
        var select = e.target;
        if (!(select instanceof Element) || !select.matches('[data-cross-sell-variant]')) return;

        var card = select.closest('[data-cross-sell-card], .oddit-cart-drawer__xsell-card');
        if (!card) return;

        var option = select.options[select.selectedIndex];
        var price = card.querySelector('.oddit-cart-drawer__xsell-price');
        if (option && price && option.dataset.price) {
          price.textContent = option.dataset.price;
        }
      }, { signal: signal });
    });

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) {
        closeDrawer();
      } else {
        trapFocus(e);
      }
    }, { signal: signal });

    // Delegated: quantity + remove on line items
    var itemContainers = queryAll('[data-cart-items]');
    itemContainers.forEach(function (container) {
      container.addEventListener('click', function (e) {
        var target = e.target;
        if (!(target instanceof Element)) return;

        var removeBtn = target.closest('[data-remove-item], [data-remove-line]');
        if (removeBtn && container.contains(removeBtn)) {
          e.preventDefault();
          var removeKey = removeBtn.getAttribute('data-line-item-key') || removeBtn.getAttribute('data-line-key');
          if (!removeKey || inFlight) return;
          setBusy(removeBtn);
          removeFromCart(removeKey).catch(function () {}).finally(function () {
            clearBusy(removeBtn);
          });
          return;
        }

        var incBtn = target.closest('[data-quantity-increase]');
        if (incBtn && container.contains(incBtn)) {
          e.preventDefault();
          if (inFlight) return;
          var incKey = incBtn.getAttribute('data-line-item-key') || incBtn.getAttribute('data-line-key');
          if (!incKey) return;
          var incItem = findItem(incKey);
          var incQty = (incItem ? incItem.quantity : parseCurrentDigit(incBtn)) + 1;
          setBusy(incBtn);
          updateLineItemQuantity(incKey, incQty).catch(function () {}).finally(function () {
            clearBusy(incBtn);
          });
          return;
        }

        var decBtn = target.closest('[data-quantity-decrease]');
        if (decBtn && container.contains(decBtn)) {
          e.preventDefault();
          if (inFlight) return;
          var decKey = decBtn.getAttribute('data-line-item-key') || decBtn.getAttribute('data-line-key');
          if (!decKey) return;
          var decItem = findItem(decKey);
          var currentQty = decItem ? decItem.quantity : parseCurrentDigit(decBtn);
          var newQty = Math.max(0, currentQty - 1);
          setBusy(decBtn);
          updateLineItemQuantity(decKey, newQty).catch(function () {}).finally(function () {
            clearBusy(decBtn);
          });
          return;
        }

        // Support generic [data-quantity-action] pattern
        var actionBtn = target.closest('[data-quantity-action]');
        if (actionBtn && container.contains(actionBtn)) {
          e.preventDefault();
          if (inFlight) return;
          var actionKey = actionBtn.getAttribute('data-line-item-key') || actionBtn.getAttribute('data-line-key');
          if (!actionKey) return;
          var action = actionBtn.getAttribute('data-quantity-action');
          var actionItem = findItem(actionKey);
          var actionCurrent = actionItem ? actionItem.quantity : parseCurrentDigit(actionBtn);
          var actionNew = action === 'increase' ? actionCurrent + 1 : Math.max(0, actionCurrent - 1);
          setBusy(actionBtn);
          updateLineItemQuantity(actionKey, actionNew).catch(function () {}).finally(function () {
            clearBusy(actionBtn);
          });
        }
      }, { signal: signal });
    });

    // Cross-sell / upsell add buttons (delegated on each root so newly rendered
    // cards still bind — but NEVER re-render the additional-checkout container).
    roots.forEach(function (root) {
      root.addEventListener('click', function (e) {
        var target = e.target;
        if (!(target instanceof Element)) return;

        // Cross-sell add buttons (marker: [data-add-to-cart] or the recommendation class)
        var addBtn = target.closest('[data-add-to-cart], .oddit-cart-drawer__rec-add, .oddit-cart-drawer__xsell-add');
        if (addBtn && root.contains(addBtn)) {
          e.preventDefault();
          if (inFlight) return;
          // The card's <select data-cross-sell-variant> wins when present: it is
          // the shopper's live choice, whereas the button's data-variant-id is
          // baked in server-side from the product's first available variant.
          var card = addBtn.closest('[data-cross-sell-card], .oddit-cart-drawer__xsell-card');
          var vSelect = card ? card.querySelector('[data-cross-sell-variant]') : null;
          var variantId = (vSelect && vSelect.value) || addBtn.getAttribute('data-variant-id');
          if (!variantId) return;
          setBusy(addBtn);
          addToCart(Number(variantId)).catch(function () {}).finally(function () {
            clearBusy(addBtn);
          });
          return;
        }

        // Bundle upsell CTA
        var upsellCta = target.closest('.oddit-cart-drawer__upsell-cta');
        if (upsellCta && root.contains(upsellCta)) {
          e.preventDefault();
          if (inFlight) return;
          var upsellVariant = upsellCta.getAttribute('data-variant-id');
          if (!upsellVariant) return;
          setBusy(upsellCta);
          addToCart(Number(upsellVariant)).catch(function () {}).finally(function () {
            clearBusy(upsellCta);
          });
          return;
        }

        // Subscription switch CTA
        var switchCta = target.closest('.oddit-cart-drawer__switch-cta');
        if (switchCta && root.contains(switchCta)) {
          e.preventDefault();
          if (inFlight) return;
          var sellingPlanId = switchCta.getAttribute('data-selling-plan-id');
          var switchKey = switchCta.getAttribute('data-line-item-key') || switchCta.getAttribute('data-line-key');
          if (!sellingPlanId) return;
          setBusy(switchCta);
          var switchPromise;
          if (switchKey) {
            switchPromise = updateLineItemToSubscription(switchKey, sellingPlanId);
          } else if (cachedCart && cachedCart.items) {
            // Apply to all applicable one-time items
            var promises = cachedCart.items
              .filter(function (item) { return !item.selling_plan_allocation; })
              .map(function (item) { return updateLineItemToSubscription(item.key, sellingPlanId); });
            switchPromise = Promise.all(promises);
          } else {
            switchPromise = Promise.resolve();
          }
          switchPromise.catch(function () {}).finally(function () {
            clearBusy(switchCta);
          });
          return;
        }

        // Checkout CTA — the standard cart form submits natively via name="checkout".
        // For any non-form checkout CTA, navigate to /checkout.
        var checkoutCta = target.closest('.oddit-cart-drawer__checkout-cta');
        if (checkoutCta && root.contains(checkoutCta) && !checkoutCta.closest('form')) {
          e.preventDefault();
          window.location.href = '/checkout';
        }
      }, { signal: signal });
    });
  }

  function findItem(key) {
    if (!cachedCart || !cachedCart.items) return null;
    for (var i = 0; i < cachedCart.items.length; i++) {
      if (cachedCart.items[i].key === key) return cachedCart.items[i];
    }
    return null;
  }

  function parseCurrentDigit(btn) {
    var group = btn.closest('.oddit-cart-drawer__quantity');
    if (!group) return 1;
    var digit = group.querySelector('[data-line-item-quantity]') ||
                group.querySelector('.oddit-cart-drawer__quantity-digit') ||
                group.querySelector('.oddit-cart-drawer__quantity-value');
    if (!digit) return 1;
    return parseInt((digit.textContent || '').trim(), 10) || 1;
  }

  // ---------- Global triggers ----------
  function bindGlobalTriggers() {
    // Any element with [data-cart-drawer-open] anywhere in the document
    document.addEventListener('click', function (e) {
      var target = e.target;
      if (!(target instanceof Element)) return;
      var opener = target.closest('[data-cart-drawer-open], [data-cart-open], .js-open-cart-drawer');
      if (opener) {
        e.preventDefault();
        openDrawer();
      }
    }, { signal: signal });

    // Listen to add-to-cart from other sections (PDP etc.) via custom event
    document.addEventListener('cart:open', function () { openDrawer(); }, { signal: signal });
    document.addEventListener('cart:close', function () { closeDrawer(); }, { signal: signal });
    document.addEventListener('cart:refresh', function () { refreshCart().catch(function () {}); }, { signal: signal });
  }

  // ---------- Theme Editor ----------
  function handleSectionUnload(e) {
    if (!e.target) return;
    var owned = false;
    for (var i = 0; i < roots.length; i++) {
      if (e.target.contains(roots[i])) { owned = true; break; }
    }
    if (!owned) return;
    destroy();
  }

  function destroy() {
    try { abortController.abort(); } catch (_) {}
    unlockBodyScroll();
    // Let the re-rendered section boot a fresh instance in the theme editor.
    window.__odditCartDrawerBooted = false;
  }

  // ---------- Init ----------
  function init() {
    bindStaticListeners();
    bindGlobalTriggers();

    // Initial cart fetch to sync currency / counts (silent — no UI reset).
    getCart().then(function (cart) {
      // Only update non-destructive bits on init so we don't blow away
      // Liquid-rendered content until we have a real cart in hand.
      renderCart(cart);
    }).catch(function () {});

    document.addEventListener('shopify:section:unload', handleSectionUnload, { signal: signal });
  }

  init();

  // ---------- Public API ----------
  window.OdditCartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    toggle: toggleDrawer,
    addToCart: addToCart,
    addMultipleToCart: addMultipleToCart,
    removeFromCart: removeFromCart,
    updateLineItemQuantity: updateLineItemQuantity,
    updateLineItemToSubscription: updateLineItemToSubscription,
    getCart: getCart,
    refreshCart: refreshCart,
    renderCart: renderCart
  };
})();
